<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisBottomSheet from 'dashboard/components-next/lais/LaisBottomSheet.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, required: true },
  description: { type: String, default: '' },
  holderName: { type: String, default: '' },
  propertyTitle: { type: String, default: '' },
  reason: { type: String, default: 'responsible_agent' },
  role: { type: String, default: 'agente_rede' },
  currentProfileRole: { type: String, default: '' },
  validationEnabled: { type: Boolean, default: false },
  keysWithAgent: { type: Boolean, default: false },
  blockedByKeyHolder: { type: Boolean, default: false },
  source: { type: String, default: 'dashboard_corretor_area' },
  drawerComponent: { type: String, required: true },
  drawerAnchor: { type: String, default: 'bottom/right' },
});

const emit = defineEmits(['close', 'go-to-visits']);

const accessDecisionLabel = computed(() =>
  props.blockedByKeyHolder ? 'Bloqueado' : 'Revisão necessária'
);

const reasonLabel = computed(() =>
  props.reason === 'responsible_agent'
    ? 'Bloqueio por corretor responsável'
    : 'Validação de chaves pendente'
);

const roleMatrixRows = computed(() => [
  {
    key: 'role',
    label: 'Papel da vitrine',
    value: props.role || 'agente_rede',
  },
  {
    key: 'profile',
    label: 'Perfil atual',
    value: props.currentProfileRole || 'Não informado',
  },
  {
    key: 'validation',
    label: 'Validação de chaves',
    value: props.validationEnabled ? 'Ativa' : 'Inativa',
  },
  {
    key: 'keys',
    label: 'Chaves com corretor',
    value: props.keysWithAgent ? 'Confirmado' : 'Não confirmado',
  },
  {
    key: 'decision',
    label: 'Resultado',
    value: accessDecisionLabel.value,
  },
]);

const auditRows = computed(() => [
  {
    key: 'drawer',
    label: 'Drawer visualizado',
    value: props.source || 'dashboard_corretor_area',
  },
  {
    key: 'decision',
    label: 'Decisão aplicada',
    value: reasonLabel.value,
  },
  {
    key: 'holder',
    label: 'Responsável',
    value: props.holderName
      ? 'Identificado e redigido em analytics'
      : 'Não informado',
  },
  {
    key: 'privacy',
    label: 'Dados sensíveis',
    value: 'Nome e endereço fora do tracking',
  },
]);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
  <LaisBottomSheet
    :show="show"
    :title="title"
    close-label="Fechar aviso de corretor responsável"
    data-lais-component="KeyHolderDrawer"
    :data-lais-chunk="drawerComponent"
    :data-lais-anchor="drawerAnchor"
    data-lais-anchor-mobile="bottom"
    data-lais-anchor-desktop="right"
    :data-key-holder-validation-enabled="validationEnabled"
    :data-role="role"
    @close="emit('close')"
  >
    <div class="space-y-4 text-[13px] leading-6 text-lais-text-muted">
      <p
        v-if="reason === 'responsible_agent'"
        class="rounded-lais border border-amber-200 bg-amber-50 px-3 py-2 font-semibold text-amber-900"
      >
        O imóvel já possui um corretor responsável.
      </p>
      <p
        v-else
        class="rounded-lais border border-lais-border bg-lais-bg-soft px-3 py-2 font-semibold text-lais-text"
      >
        Validação de chaves necessária.
      </p>

      <div class="space-y-2">
        <p>{{ description }}</p>
        <p v-if="holderName" class="font-semibold text-lais-text">
          Responsável: {{ holderName }}
        </p>
        <p v-if="propertyTitle" class="text-lais-text">
          Imóvel: {{ propertyTitle }}
        </p>
      </div>

      <section
        aria-labelledby="key-holder-role-matrix-title"
        data-key-holder-role-matrix
      >
        <h3
          id="key-holder-role-matrix-title"
          class="text-[12px] font-semibold uppercase text-lais-text-muted"
        >
          Matriz de acesso
        </h3>
        <dl class="mt-2 grid gap-2 sm:grid-cols-2">
          <div
            v-for="row in roleMatrixRows"
            :key="row.key"
            class="rounded-lais-sm border border-lais-border bg-white px-3 py-2"
          >
            <dt
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              {{ row.label }}
            </dt>
            <dd class="mt-1 font-semibold text-lais-text">{{ row.value }}</dd>
          </div>
        </dl>
      </section>

      <section
        aria-labelledby="key-holder-audit-ledger-title"
        data-key-holder-audit-ledger
      >
        <h3
          id="key-holder-audit-ledger-title"
          class="text-[12px] font-semibold uppercase text-lais-text-muted"
        >
          Trilha auditável
        </h3>
        <dl class="mt-2 grid gap-2">
          <div
            v-for="row in auditRows"
            :key="row.key"
            class="rounded-lais-sm bg-lais-bg-soft px-3 py-2"
          >
            <dt
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              {{ row.label }}
            </dt>
            <dd class="mt-1 font-semibold text-lais-text">{{ row.value }}</dd>
          </div>
        </dl>
      </section>
    </div>

    <template #footer>
      <div class="flex flex-wrap justify-end gap-2">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          Entendi
        </LaisPrimaryButton>
        <LaisPrimaryButton
          icon="i-lucide-calendar-plus"
          @click="emit('go-to-visits')"
        >
          Revisar visita
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisBottomSheet>
</template>
